// Salesforce Open CTI global injected via opencti_min.js loaded from the parent Salesforce org
// https://developer.salesforce.com/docs/atlas.en-us.api_cti.meta/api_cti/sforce_api_cti_methods_intro_lightning.htm

export type SalesforceClickToDialPayload = {
  number: string;
  recordName?: string;
  recordId?: string;
  objectType?: string;
};

declare global {
  interface Window {
    sforce?: {
      opencti: {
        enableClickToDial: (options: {
          callback: (response: { success: boolean }) => void;
        }) => void;
        onClickToDial: (options: {
          listener: (payload: SalesforceClickToDialPayload) => void;
        }) => void;
        setSoftphonePanelVisibility: (options: {
          visible: boolean;
          callback?: (response: { success: boolean; errors?: unknown }) => void;
        }) => void;
      };
    };
  }
}

// Allowed Salesforce domain suffixes for CTI integration.
// Standard "My Domain" URLs (e.g., acme.my.salesforce.com) are covered by '.salesforce.com'.
// If a customer uses a custom Salesforce domain not matching these suffixes (e.g., Government Cloud),
// they should contact support to have their domain added to this allowlist.
const SALESFORCE_DOMAINS = [
  '.salesforce.com',
  '.force.com',
  '.lightning.force.com',
  '.visualforce.com',
  '.salesforce-sites.com',
];

// Open CTI API version — update when adopting new API features.
// Release notes: https://developer.salesforce.com/docs/atlas.en-us.api_cti.meta/api_cti/sforce_api_cti_changelog.htm
const OPEN_CTI_API_VERSION = '58.0';

type EventHandler = (data?: unknown) => void;

class SalesforceService {
  private isInitialized = false;
  private initPromise: Promise<boolean> | null = null;
  private eventHandlers: Map<string, EventHandler[]> = new Map();

  private getSalesforceOrigin(): string | null {
    // Method 1: Check document.referrer
    try {
      const referrer = document.referrer;
      if (referrer) {
        const url = new URL(referrer);
        if (SALESFORCE_DOMAINS.some((domain) => url.hostname.endsWith(domain))) {
          return url.origin;
        }
      }
    } catch {
      // ignore
    }

    // Method 2: Check ancestorOrigins (Chromium browsers)
    try {
      const ancestorOrigins = window.location.ancestorOrigins;
      if (ancestorOrigins?.length > 0) {
        for (let i = 0; i < ancestorOrigins.length; i++) {
          const origin = ancestorOrigins.item(i);
          if (!origin) {
            continue;
          }
          const { hostname } = new URL(origin);
          if (SALESFORCE_DOMAINS.some((domain) => hostname.endsWith(domain))) {
            return origin;
          }
        }
      }
    } catch {
      // ignore
    }

    return null;
  }

  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    // Prevent concurrent initialization (e.g., React 18 StrictMode double-effect)
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInitialize().then((result) => {
      if (!result) {
        this.initPromise = null;
      }
      return result;
    });
    return this.initPromise;
  }

  private async doInitialize(): Promise<boolean> {
    const salesforceOrigin = this.getSalesforceOrigin();
    if (!salesforceOrigin) {
      return false;
    }

    try {
      await this.loadOpenCtiScript(salesforceOrigin);
    } catch (error) {
      console.error('[Salesforce] Failed to load opencti_min.js:', error);
      return false;
    }

    return this.enableClickToDial();
  }

  private loadOpenCtiScript(salesforceOrigin: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.sforce?.opencti) {
        resolve();
        return;
      }

      try {
        const scriptUrl = `${salesforceOrigin}/support/api/${OPEN_CTI_API_VERSION}/lightning/opencti_min.js`;

        const script = document.createElement('script');
        script.src = scriptUrl;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load opencti_min.js from ${scriptUrl}`));
        document.head.appendChild(script);
      } catch (error) {
        reject(error);
      }
    });
  }

  private enableClickToDial(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!window.sforce?.opencti) {
        console.error('[Salesforce] sforce.opencti not available after script load');
        resolve(false);
        return;
      }

      window.sforce.opencti.enableClickToDial({
        callback: (response) => {
          if (response.success) {
            this.isInitialized = true;
            this.setupClickToDialListener();
            console.info('[Salesforce] Click-to-Dial enabled');
            resolve(true);
          } else {
            console.error('[Salesforce] Failed to enable Click-to-Dial');
            resolve(false);
          }
        },
      });
    });
  }

  private setupClickToDialListener() {
    const opencti = window.sforce?.opencti;
    if (!opencti) {
      console.error('[Salesforce] Cannot setup listener: sforce.opencti not available');
      return;
    }

    opencti.onClickToDial({
      listener: (payload) => {
        opencti.setSoftphonePanelVisibility({
          visible: true,
          callback: (response) => {
            if (!response.success) {
              console.error('[Salesforce] Failed to open softphone panel', response.errors);
            }
          },
        });
        this.emit('dialNumber', payload);
      },
    });
  }

  on(event: string, handler: EventHandler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)?.push(handler);
  }

  off(event: string, handler: EventHandler) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: unknown) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        handler(data);
      }
    }
  }
}

export const salesforceService = new SalesforceService();
